import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const persistUser = (u) => {
    localStorage.setItem('clippost_user', JSON.stringify(u));
    setUser(u);
  };

  const refreshUser = useCallback(async () => {
    try {
      const res = await authAPI.me();
      persistUser(res.data);
      return res.data;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('clippost_token');
      const savedUser = localStorage.getItem('clippost_user');
      if (token) {
        if (savedUser) {
          try { setUser(JSON.parse(savedUser)); } catch { /* ignore */ }
        }
        // Always refetch fresh user (with live credit counts) on mount
        try {
          const res = await authAPI.me();
          persistUser(res.data);
        } catch {
          localStorage.removeItem('clippost_token');
          localStorage.removeItem('clippost_user');
          setUser(null);
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login({ email, password });
    localStorage.setItem('clippost_token', res.data.token);
    persistUser(res.data.user);
    // Fetch full user with credits
    try {
      const meRes = await authAPI.me();
      persistUser(meRes.data);
    } catch { /* ignore */ }
    return res.data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const res = await authAPI.register({ name, email, password });
    localStorage.setItem('clippost_token', res.data.token);
    persistUser(res.data.user);
    try {
      const meRes = await authAPI.me();
      persistUser(meRes.data);
    } catch { /* ignore */ }
    return res.data;
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates };
      localStorage.setItem('clippost_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('clippost_token');
    localStorage.removeItem('clippost_user');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
